import { normalizeToUTCMidnight } from '@omnea-app/packages/universal/date-utils/normalizeToUTCMidnight'
import { PaymentIntervalEnum } from '@omnea-app/packages/universal/hasura/graphql'

interface WithDateRange {
  startDate: Date | string
  endDate: Date | string | null
  frequency: PaymentIntervalEnum
}

/**
 * Adjusts a spend item's end date based on a new end date boundary.
 * - If the new end date is before the spend item's end date, truncates the spend item to end at the new date.
 * - If the spend item starts after the new end date, returns null.
 * - If the spend item already ends before or on the new end date, no changes are made.
 * All date comparisons are done without timezone considerations (date-only).
 */
export const adjustSpendItemEndDate = <T extends WithDateRange>(spendItem: T, endDate: Date): T | null => {
  // Normalize dates to UTC midnight for date-only comparison.
  // This ensures two dates representing the same calendar day always compare as equal,
  // even if they have different time-of-day values (e.g., UTC midnight vs local midnight).
  const normalizedEndDate = normalizeToUTCMidnight(endDate)
  const normalizedSpendItemStartDate = normalizeToUTCMidnight(new Date(spendItem.startDate))
  const normalizedSpendItemEndDate = spendItem.endDate !== null ? normalizeToUTCMidnight(new Date(spendItem.endDate)) : null

  // If spendItem's startDate is after endDate, return null (spend item should be deleted)
  if (normalizedSpendItemStartDate > normalizedEndDate) {
    return null
  }

  // If the spend item is a one-off, don't adjust and leave it unchanged
  if (spendItem.frequency === PaymentIntervalEnum.ONE_OFF) {
    return spendItem
  }

  // If endDate is before spendItem's endDate, adjust the endDate
  if (normalizedSpendItemEndDate === null || normalizedEndDate < normalizedSpendItemEndDate) {
    return { ...spendItem, endDate }
  }

  // Otherwise, leave it unchanged
  return spendItem
}
